import React from "react";

interface RegionCell {
  name: string;
  rate: string;
  loans: string;
  avgLTV: string;
  riskLevel: "low" | "mid" | "high";
}

const regionData: RegionCell[] = [
  {
    name: "Toronto CMA",
    rate: "1.8%",
    loans: "1,136 loans",
    avgLTV: "avg LTV 74%",
    riskLevel: "low",
  },
  {
    name: "Vancouver CMA",
    rate: "2.1%",
    loans: "923 loans",
    avgLTV: "avg LTV 78%",
    riskLevel: "low",
  },
  {
    name: "Calgary CMA",
    rate: "3.4%",
    loans: "412 loans",
    avgLTV: "avg LTV 79%",
    riskLevel: "mid",
  },
  {
    name: "Montreal CMA",
    rate: "2.3%",
    loans: "734 loans",
    avgLTV: "avg LTV 76%",
    riskLevel: "low",
  },
  {
    name: "Edmonton CMA",
    rate: "3.7%",
    loans: "267 loans",
    avgLTV: "avg LTV 80%",
    riskLevel: "mid",
  },
  {
    name: "Halifax CMA",
    rate: "4.1%",
    loans: "112 loans",
    avgLTV: "avg LTV 81%",
    riskLevel: "high",
  },
];

interface MapNode {
  city: string;
  rate: string;
  top: string;
  left: string;
  riskLevel: "low" | "mid" | "high";
}

const mapNodes: MapNode[] = [
  {
    city: "Vancouver",
    rate: "2.1%",
    top: "26%",
    left: "30%",
    riskLevel: "low",
  },
  { city: "Calgary", rate: "3.4%", top: "35%", left: "55%", riskLevel: "low" },
  { city: "Edmonton", rate: "3.7%", top: "40%", left: "65%", riskLevel: "low" },
  { city: "Winnipeg", rate: "3.2%", top: "44%", left: "72%", riskLevel: "low" },
  { city: "Toronto", rate: "1.8%", top: "40%", left: "82%", riskLevel: "low" },
  { city: "Ottawa", rate: "1.9%", top: "45%", left: "88%", riskLevel: "low" },
  { city: "Montreal", rate: "2.3%", top: "48%", left: "86%", riskLevel: "low" },
  { city: "Halifax", rate: "4.1%", top: "55%", left: "90%", riskLevel: "high" },
];

export const RegionalHeatMap: React.FC = () => {
  return (
    <div className="relative p-4 pb-4.5 rounded-[18px] bg-gradient-to-br from-[#22284d] to-[#10142f] border border-[rgba(139,146,176,0.2)] shadow-[0_16px_40px_rgba(0,0,0,0.6)] overflow-hidden">
      <div className="flex items-center justify-between gap-2 mb-2.5">
        <div className="flex flex-col gap-0.5">
          <div className="text-[15px] font-medium tracking-[0.06em] uppercase">
            Regional heat map
          </div>
          <div className="text-[11px] text-[#8b92b0]">
            Default rate hotspots, normalized by loan count and outstanding
            balance.
          </div>
        </div>
        <span className="px-2.25 py-0.75 rounded-full text-[10px] uppercase tracking-[0.12em] border border-[rgba(0,255,136,0.8)] text-neon-green bg-[rgba(0,255,136,0.12)]">
          Stable overall
        </span>
      </div>

      <div className="mt-1.5 rounded-[12px] bg-gradient-to-b from-[#1f2849] to-[#050716] border border-[rgba(139,146,176,0.35)] p-2.5 pb-3.5 relative overflow-hidden">
        <div className="flex items-center justify-between gap-2.5 mb-1.5 text-[11px] text-[#8b92b0] flex-wrap">
          <div className="flex items-center gap-2.5 flex-wrap">
            <span className="inline-flex items-center gap-0.75">
              <span className="w-2.25 h-2.25 rounded-full bg-[rgba(0,255,136,0.95)] mr-1" />
              Low risk &lt; 2.5%
            </span>
            <span className="inline-flex items-center gap-0.75">
              <span className="w-2.25 h-2.25 rounded-full bg-[rgba(255,184,0,0.95)] mr-1" />
              Medium 2.5–3.5%
            </span>
            <span className="inline-flex items-center gap-0.75">
              <span className="w-2.25 h-2.25 rounded-full bg-[rgba(255,51,102,0.95)] mr-1" />
              High &gt; 3.5%
            </span>
          </div>
          <div>
            Weighted average (national): <strong>2.4%</strong>
          </div>
        </div>

        <div className="relative h-[260px] rounded-[10px] overflow-hidden bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_right,rgba(255,255,255,0.04)_1px,transparent_1px)] bg-[length:40px_40px]">
          <div className="absolute inset-[18%_8%_16%] bg-[radial-gradient(circle_at_15%_15%,#00d4ff33_0,transparent_40%),radial-gradient(circle_at_50%_25%,#00ff8833_0,transparent_45%),radial-gradient(circle_at_80%_40%,#ffb80033_0,transparent_45%),radial-gradient(circle_at_35%_75%,#ff336633_0,transparent_45%)] mix-blend-screen opacity-90" />
          <div className="absolute inset-[18%_8%_16%] rounded-[24%_76%_55%_45%_/_32%_33%_67%_68%] border border-dashed border-white/25 backdrop-blur-[10px] opacity-70" />

          {mapNodes.map((node, idx) => (
            <div
              key={idx}
              className={`absolute w-3 h-3 rounded-full border-2 border-black/80 shadow-[0_0_16px_rgba(0,0,0,0.7)] ${
                node.riskLevel === "low"
                  ? "bg-[rgba(0,255,136,0.95)]"
                  : node.riskLevel === "mid"
                  ? "bg-[rgba(255,184,0,0.95)]"
                  : "bg-[rgba(255,51,102,0.95)]"
              }`}
              style={{ top: node.top, left: node.left }}
            >
              <span className="absolute top-[-18px] left-1/2 -translate-x-1/2 whitespace-nowrap text-[11px] text-white drop-shadow-[0_0_6px_rgba(0,0,0,0.8)]">
                {node.city} · {node.rate}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-1.5 max-[640px]:grid-cols-2">
        {regionData.map((region, idx) => (
          <div
            key={idx}
            className={`relative p-2 pb-2.25 rounded-[10px] bg-[rgba(5,7,22,0.9)] border border-[rgba(139,146,176,0.35)] overflow-hidden cursor-pointer transition-all duration-150 hover:border-[rgba(0,212,255,0.7)] hover:-translate-y-0.5 hover:shadow-[0_12px_34px_rgba(0,0,0,0.6)] text-[11px] ${
              region.riskLevel === "mid"
                ? "before:absolute before:inset-0 before:opacity-30 before:mix-blend-screen before:bg-gradient-to-br before:from-transparent before:to-[rgba(255,184,0,0.7)]"
                : region.riskLevel === "high"
                ? "before:absolute before:inset-0 before:opacity-30 before:mix-blend-screen before:bg-gradient-to-br before:from-transparent before:to-[rgba(255,51,102,0.9)]"
                : "before:absolute before:inset-0 before:opacity-30 before:mix-blend-screen before:bg-gradient-to-br before:from-transparent before:to-[rgba(0,212,255,0.5)]"
            }`}
          >
            <div className="flex items-center justify-between gap-1 relative z-[1]">
              <span className="text-[11px] font-medium">{region.name}</span>
              <span
                className={`w-2 h-2 rounded-full border border-black/80 ${
                  region.riskLevel === "low"
                    ? "bg-[rgba(0,255,136,0.9)]"
                    : region.riskLevel === "mid"
                    ? "bg-[rgba(255,184,0,0.9)]"
                    : "bg-[rgba(255,51,102,0.9)]"
                }`}
              />
            </div>
            <div className="font-jetbrains-mono text-[13px] font-semibold mt-0.5 relative z-[1]">
              {region.rate}
            </div>
            <div className="text-[10px] text-[#8b92b0] mt-0.25 relative z-[1]">
              {region.loans} · {region.avgLTV}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
